import { ArrowLeft, Play, Clock, Flame, TrendingUp } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

const ExerciseVideos = () => {
  const navigate = useNavigate();

  const videos = [
    {
      id: 1,
      title: "Morning Yoga Flow",
      duration: "15 min",
      calories: "80 cal",
      difficulty: "Beginner",
      thumbnail:
        "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=250&fit=crop",
      category: "Yoga",
    },
    {
      id: 2,
      title: "HIIT Full Body Workout",
      duration: "20 min",
      calories: "250 cal",
      difficulty: "Advanced",
      thumbnail:
        "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=250&fit=crop",
      category: "HIIT",
    },
    {
      id: 3,
      title: "Core Strength Training",
      duration: "12 min",
      calories: "120 cal",
      difficulty: "Intermediate",
      thumbnail:
        "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=250&fit=crop",
      category: "Strength",
    },
    {
      id: 4,
      title: "Stretching & Flexibility",
      duration: "10 min",
      calories: "50 cal",
      difficulty: "Beginner",
      thumbnail:
        "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=250&fit=crop",
      category: "Flexibility",
    },
    {
      id: 5,
      title: "Cardio Dance Workout",
      duration: "25 min",
      calories: "300 cal",
      difficulty: "Intermediate",
      thumbnail:
        "https://images.unsplash.com/photo-1524594152303-9fd13543fe6e?w=400&h=250&fit=crop",
      category: "Cardio",
    },
    {
      id: 6,
      title: "Pilates Mat Workout",
      duration: "18 min",
      calories: "140 cal",
      difficulty: "Intermediate",
      thumbnail:
        "https://images.unsplash.com/photo-1518459384564-fb3c8c937d9e?w=400&h=250&fit=crop",
      category: "Pilates",
    },
    {
      id: 7,
      title: "Upper Body Strength",
      duration: "22 min",
      calories: "200 cal",
      difficulty: "Advanced",
      thumbnail:
        "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=250&fit=crop",
      category: "Strength",
    },
    {
      id: 8,
      title: "Meditation & Mindfulness",
      duration: "15 min",
      calories: "30 cal",
      difficulty: "Beginner",
      thumbnail:
        "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=250&fit=crop",
      category: "Mindfulness",
    },
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner":
        return "bg-chart-2/10 text-chart-2 border-chart-2/20";
      case "Intermediate":
        return "bg-accent/10 text-accent border-accent/20";
      case "Advanced":
        return "bg-destructive/10 text-destructive border-destructive/20";
      default:
        return "bg-muted/10 text-muted-foreground border-muted/20";
    }
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Exercise Videos</h1>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
        {/* Header Stats */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-primary/5 to-primary/10 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                {videos.length}
              </h2>
              <p className="text-sm text-muted-foreground">Exercise Videos</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
              <Play className="h-6 w-6 text-primary" />
            </div>
          </div>
        </Card>

        {/* Video Grid */}
        <div className="space-y-3">
          {videos.map(video => (
            <Card
              key={video.id}
              className="cursor-pointer overflow-hidden rounded-2xl border-0 shadow-sm transition-all hover:scale-[1.02] hover:shadow-lg"
            >
              <div className="relative">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="h-40 w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 to-transparent">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background/90 backdrop-blur-sm transition-transform hover:scale-110 dark:bg-white/90">
                    <Play className="ml-1 h-6 w-6 text-primary" />
                  </div>
                </div>
                <Badge className="absolute right-2 top-2 bg-background/90 backdrop-blur-sm">
                  {video.category}
                </Badge>
              </div>
              <div className="p-4">
                <h3 className="mb-2 font-semibold text-foreground">
                  {video.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {video.duration}
                  </div>
                  <div className="flex items-center gap-1">
                    <Flame className="h-3.5 w-3.5" />
                    {video.calories}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-xs ${getDifficultyColor(video.difficulty)}`}
                  >
                    {video.difficulty}
                  </Badge>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Weekly Goal */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              Weekly Exercise Goal
            </h3>
            <TrendingUp className="h-5 w-5 text-chart-2" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Videos Completed</span>
              <span className="font-semibold text-foreground">4 / 5</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-muted/30">
              <div
                className="h-2.5 rounded-full bg-chart-2"
                style={{ width: "80%" }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Great progress! Just 1 more video to reach your weekly goal.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ExerciseVideos;
